import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import {
  PlaybookApiError,
  useApiClient,
  useModuleAvailability,
  useShellBridge,
  workLinksApi,
} from "@playbookai/api-clients";
import { vsmKpiLinksApi } from "../api/vsm-kpi-links.js";
import type { WorkLinkWithStatus } from "@playbookai/types";
import { Button, LinkedWorkPanel, type LinkedWorkRow } from "@playbookai/ui";
import { KpiPanel } from "./KpiPanel.js";

const MAX_LINK_PAGES = 50;

type KpiWorkLink = WorkLinkWithStatus & {
  readonly targetRef:
    | { readonly refName: "solve"; readonly refId: string }
    | { readonly refName: "vsm"; readonly refId: string };
};

function isKpiWorkLink(link: WorkLinkWithStatus): link is KpiWorkLink {
  return link.targetRef.refName === "solve" || link.targetRef.refName === "vsm";
}

function statusFor(
  link: KpiWorkLink,
): NonNullable<LinkedWorkRow["status"]> | undefined {
  if (!link.work?.status) return undefined;
  const tone =
    link.work.statusCategory === "done"
      ? "healthy"
      : link.work.statusCategory === "open"
        ? "info"
        : "neutral";
  return { label: link.work.status, tone };
}

/**
 * SOLVEs and value streams connected to one KPI through the platform work-link
 * contract. Read-only here: either work surface or its strategy surface owns
 * creation, while the KPI page makes the resulting relationship visible.
 */
export function KpiConnectedWork({
  kpiId,
}: {
  readonly kpiId: string;
}): JSX.Element | null {
  const config = useApiClient();
  const bridge = useShellBridge();
  // ADR-0100: VSM's own backlinks are not asked for while VSM is off, and
  // links into an off module are one greyed line with no Open (d3).
  const availability = useModuleAvailability();
  const vsmAvailable = availability.isAvailable("vsm");
  const query = useQuery({
    queryKey: ["work-links", "kpi", kpiId],
    queryFn: async () => {
      const links: WorkLinkWithStatus[] = [];
      const seenCursors = new Set<string>();
      let cursor: string | undefined;
      for (let page = 0; page < MAX_LINK_PAGES; page += 1) {
        const response = await workLinksApi.listWorkLinks(config, {
          refName: "kpi",
          refId: kpiId,
          ...(cursor ? { cursor } : {}),
        });
        links.push(...response.data);
        if (!response.hasMore) return links.filter(isKpiWorkLink);
        const next = response.nextCursor;
        if (!next || seenCursors.has(next)) {
          throw new Error("Couldn’t load the complete connected-work list.");
        }
        seenCursors.add(next);
        cursor = next;
      }
      throw new Error("Connected-work page limit reached.");
    },
  });
  const vsmBacklinks = useQuery({
    queryKey: ["vsm-kpi-links", "kpi", kpiId],
    enabled: vsmAvailable,
    queryFn: async () => {
      try {
        return await vsmKpiLinksApi.listForKpi(config, kpiId);
      } catch (error) {
        // Keep existing SOLVE/work-link rows visible during a rolling deploy
        // where the shell is newer than the VSM backlink endpoint.
        if (error instanceof PlaybookApiError && error.status === 404)
          return [];
        throw error;
      }
    },
  });

  const links = useMemo(() => query.data ?? [], [query.data]);
  // VSM's own KPI links (`/v1/vsms/by-kpi/:id`) are VSM's API, so while VSM is
  // off they are not read — and so not counted in the line below either.
  const linkedVsms = useMemo(
    () => (vsmAvailable ? (vsmBacklinks.data ?? []) : []),
    [vsmAvailable, vsmBacklinks.data],
  );
  // A platform link into a module that is off has no name to show (the server
  // leaves `work.title` out), so each such module is one greyed line.
  const { shownLinks, hiddenNotes } = useMemo(() => {
    const shown: KpiWorkLink[] = [];
    const hidden = { solve: 0, vsm: 0 };
    for (const link of links) {
      if (availability.isAvailable(link.targetRef.refName)) shown.push(link);
      else hidden[link.targetRef.refName] += 1;
    }
    return {
      shownLinks: shown,
      hiddenNotes: (["solve", "vsm"] as const).flatMap((key) => {
        const note = availability.hiddenLinksMessage(key, hidden[key]);
        return note ? [note] : [];
      }),
    };
  }, [availability, links]);
  const rows = useMemo<readonly LinkedWorkRow[]>(() => {
    const platformVsmIds = new Set(
      links
        .filter((link) => link.targetRef.refName === "vsm")
        .map((link) => link.targetRef.refId),
    );
    const platformRows = shownLinks.map((link) => {
      const title =
        link.work?.title?.trim() ||
        (link.targetRef.refName === "solve"
          ? "Linked SOLVE"
          : "Linked value stream");
      const status = statusFor(link);
      const startedFromWork =
        link.initiatedFrom === "work" ||
        link.initiatedFrom === "solve" ||
        link.initiatedFrom === "vsm";
      return {
        id: link.id,
        kind: link.targetRef.refName,
        title,
        ...(status ? { status } : {}),
        ...(startedFromWork ? { backlinked: true } : {}),
        ...(link.note ? { note: link.note } : {}),
      };
    });
    const vsmRows = linkedVsms
      .filter((vsm) => !platformVsmIds.has(vsm.vsmId))
      .map<LinkedWorkRow>((vsm) => ({
        id: `vsm-kpi:${vsm.vsmId}`,
        kind: "vsm",
        title: vsm.vsmName,
        backlinked: true,
      }));
    return [...platformRows, ...vsmRows];
  }, [links, linkedVsms, shownLinks]);

  const rowAction = (row: LinkedWorkRow): JSX.Element | null => {
    const vsmBacklink = linkedVsms.find(
      (candidate) => `vsm-kpi:${candidate.vsmId}` === row.id,
    );
    if (vsmBacklink && bridge.openModuleEntityTab) {
      return (
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Open ${row.title}`}
          onClick={() =>
            bridge.openModuleEntityTab?.({
              moduleId: "vsm",
              entityId: vsmBacklink.vsmId,
              title: row.title,
            })
          }
        >
          Open
          <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      );
    }
    const link = links.find((candidate) => candidate.id === row.id);
    if (!link) return null;
    if (link.targetRef.refName === "solve" && bridge.openSolve) {
      return (
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Open ${row.title}`}
          onClick={() =>
            bridge.openSolve?.({
              solveId: link.targetRef.refId,
              title: row.title,
            })
          }
        >
          Open
          <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      );
    }
    if (link.targetRef.refName === "vsm" && bridge.openModuleEntityTab) {
      return (
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Open ${row.title}`}
          onClick={() =>
            bridge.openModuleEntityTab?.({
              moduleId: "vsm",
              entityId: link.targetRef.refId,
              title: row.title,
            })
          }
        >
          Open
          <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      );
    }
    return null;
  };

  // With SOLVE and VSM both off and no platform link, this section is about
  // nothing the workspace has: no heading, no empty state.
  if (
    links.length === 0 &&
    !availability.isAvailable("solve") &&
    !vsmAvailable
  ) {
    return null;
  }

  return (
    <KpiPanel
      title="Connected work"
      headingId={`kpi-connected-work-${kpiId}`}
      subtitle="SOLVEs and value streams linked to this KPI."
    >
      <LinkedWorkPanel
        heading="SOLVEs and value streams"
        rows={rows}
        hiddenNotes={hiddenNotes}
        isLoading={query.isLoading || (vsmAvailable && vsmBacklinks.isLoading)}
        error={
          query.isError || (vsmAvailable && vsmBacklinks.isError)
            ? "Couldn’t load connected work."
            : null
        }
        rowActionSlot={rowAction}
      />
    </KpiPanel>
  );
}
